/* UsersCrudCard.tsx
	Une card affichant la gestion CRUD des utilisateurs
	Utilise le composant GenericCrudCard pour lister, ajouter, éditer et supprimer les éléments
	Utilise listPaginated pour bénéficier de la pagination, du tri et des filtres côté serveur
*/

// Import React
import React from 'react';

// Import FontAwesome
import { faUsers, faStar, faList } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

// Import Communs
import { GenericCrudCard } from 'common';

// Import Feature
import { UserData, usersApi } from 'features/users';

// Mapping des couleurs de badge par statut
const statusColors: Record<string, string> = {
	active: 'bg-green-100 text-green-700',
	inactive: 'bg-yellow-100 text-yellow-800',
	suspended: 'bg-red-100 text-red-700',
};

// Mapping des libellés de statut
const statusLabels: Record<string, string> = {
	active: 'Actif',
	inactive: 'Inactif',
	suspended: 'Suspendu',
};

// Interface des props du composant
interface UsersCrudCardProps {
	size?: 'sm' | 'md' | 'lg';
	returnPath?: string;
	returnLabel?: string;
	returnIcon?: IconDefinition;
	returnInvert?: boolean;
	className?: string;
	/** Affiche ou masque l'icône dans l'en-tête de la card. Défaut : true */
	showIcon?: boolean;
	/** Masque le bouton retour. Utile quand la card est intégrée dans une page avec son propre bouton retour. Défaut : false */
	hideReturn?: boolean;
}

// UsersCrudCard component
export const UsersCrudCard: React.FC<UsersCrudCardProps> = ({
	size = 'sm',
	returnPath = '/app/dashboard',
	returnLabel,
	returnIcon,
	returnInvert = false,
	className,
	showIcon = true,
	hideReturn = false,
}) => {
	// Détermination si on utilise la version étendue (size !== 'sm')
	const extended = size !== 'sm';
	// Titre et icône de la card selon la taille :
	// - sm : titre court + icône métier faUsers (pour les contextes embarqués)
	// - lg/md : titre long "Tableau dynamique" + icône faTable (masquée par showIcon={false} sur la page)
	const cardTitle = size === 'sm' ? 'Utilisateurs' : 'Liste des utilisateurs';
	const cardIcon = size === 'sm' ? faUsers : faList;

	// Définition des colonnes du tableau
	const columns = [
		// Colonne "ID" uniquement si extended — affichée en "USR-{id}", tri désactivé
		...(extended
			? [
					{
						label: 'ID',
						field: 'none',
						render: (a: UserData) => (
							<span className="text-gray-400">USR-{a.id}</span>
						),
					},
				]
			: []),
		// Colonne "État" — badge coloré selon le statut, tri désactivé (enum)
		{
			label: 'État',
			field: 'none',
			render: (a: UserData) => (
				<span
					className={`inline-block px-2 py-0.5 text-xs rounded-full font-medium ${statusColors[a.status ?? ''] ?? 'bg-gray-100 text-gray-500'}`}
				>
					{statusLabels[a.status ?? ''] ?? '—'}
				</span>
			),
		},
		// Colonne "Prénom"
		{
			label: 'Prénom',
			field: 'firstName',
			render: (a: UserData) => a.firstName ?? '—',
		},
		// Colonne "Nom" — avec étoile dorée si l'utilisateur est administrateur (ROLE_ADMIN)
		{
			label: 'Nom',
			field: 'name',
			render: (a: UserData) => (
				<span className="flex items-center gap-1.5">
					{a.name ?? '—'}
					{/* Indicateur administrateur */}
					{a.roles?.includes('ROLE_ADMIN') && (
						<FontAwesomeIcon
							icon={faStar}
							className="text-yellow-400"
							title="Administrateur"
							size="xs"
						/>
					)}
				</span>
			),
		},
		// Colonne "Email"
		{
			label: 'Email',
			field: 'email',
			render: (a: UserData) => a.email ?? '—',
		},
	];

	return (
		<GenericCrudCard<UserData>
			queryKey="users"
			title={cardTitle}
			fetchData={(token, options) => usersApi.listPaginated(token, options)}
			deleteItem={usersApi.delete}
			tableColumns={columns}
			addButtonLabel="Ajouter un utilisateur"
			getAddPath={() => '/app/users/new'}
			getEditPath={(id) => `/app/users/${id}/edit`}
			emptyMessage="Aucun utilisateur trouvé."
			getReturnPath={hideReturn ? undefined : () => returnPath}
			returnLabel={returnLabel}
			returnIcon={returnIcon}
			returnInvert={returnInvert}
			size={size}
			icon={cardIcon}
			className={className}
			showIcon={showIcon}
		/>
	);
};

export default UsersCrudCard;
